/**
 * History Presenter Component
 * Presentational component for history page
 */

import React from 'react';
import { ThemeProvider } from '@/design-system/themes';
import { Sidebar } from '@/components/navbar/web/Sidebar';
import { MobileNavbar } from '@/components/navbar/mobile/MobileNavbar';
import { SearchEntry } from '@/store/chatHistoryStore';
import { HistoryHeader, HistorySearchBar, HistoryList } from './';
import { backgroundColors, spacing } from '@/design-system/utils';

export interface HistoryPresenterProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filteredHistory: SearchEntry[];
  isLoading?: boolean;
  onNewChat: () => void;
  onLogoClick: () => void;
  onEntryClick: (index: number) => void;
  onShare?: (index: number) => void;
  onDelete?: (index: number) => void;
}

export const HistoryPresenter: React.FC<HistoryPresenterProps> = ({
  searchQuery,
  onSearchChange,
  filteredHistory,
  isLoading = false,
  onNewChat,
  onLogoClick,
  onEntryClick,
  onShare,
  onDelete,
}) => {
  return (
    <ThemeProvider defaultMode='light'>
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: backgroundColors.primary,
          display: 'flex',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Sidebar */}
        <Sidebar onNewChat={onNewChat} onLogoClick={onLogoClick} />

        {/* Mobile Navbar */}
        <div className='md:hidden'>
          <MobileNavbar onNewChat={onNewChat} onLogoClick={onLogoClick} />
        </div>

        {/* Main Content */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            position: 'relative',
          }}
          className='md:ml-[111px] pt-[56px] md:pt-[64px]'
        >
          <div
            style={{
              width: '100%',
              maxWidth: '761px',
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[6],
              alignItems: 'center',
              paddingTop: spacing[6],
              paddingBottom: spacing[6],
              paddingLeft: spacing[4],
              paddingRight: spacing[4],
            }}
            className='md:px-[320px] md:gap-[32px] md:py-4'
          >
            <HistoryHeader />
            <HistorySearchBar value={searchQuery} onChange={onSearchChange} />
            <HistoryList
              entries={filteredHistory}
              searchQuery={searchQuery}
              isLoading={isLoading}
              onEntryClick={onEntryClick}
              onShare={onShare}
              onDelete={onDelete}
            />
          </div>
        </main>
      </div>
    </ThemeProvider>
  );
};
